/**
 * LacedUpChi — Motion System V1
 *
 * Reveals content as it enters the viewport. That is the whole job.
 *
 * CONTRACT WITH THE INLINE GATE
 * layout/theme.liquid sets `data-motion="on"` on <html> before first
 * paint, and only when the visitor has NOT asked for reduced motion and
 * IntersectionObserver exists. assets/motion.css hides the reveal
 * targets only under that attribute, so this script is never what makes
 * content visible in the first place — it is what makes it *animate*.
 *
 * The gate also arms a 2s failsafe that removes the attribute if this
 * file never runs. Calling window.__lucMotionReady() cancels it. If this
 * script is blocked, 404s, or throws before that call, the failsafe
 * fires and every reveal target becomes visible. Content is never
 * dependent on this file succeeding.
 *
 * SELECTOR SYNC
 * REVEAL_SELECTOR below is duplicated in assets/motion.css §2 and §7.
 * The CSS has to name real component classes because a JS-applied hook
 * cannot exist at first paint and would flash. Change one, change both.
 *
 * Deliberately NOT here: scroll hijacking, parallax, counters, autoplay,
 * loading theatrics, and any element that repeats its entrance. Each
 * element animates exactly once and is then unobserved.
 */
(function () {
  'use strict';

  var REVEAL_SELECTOR = [
    '.home-section-heading',
    '.home-product-grid__item',
    '.home-collections',
    '.block-directory__head',
    '.block-directory__list',
    '.collection__header',
    '.collection__grid-item',
    '.product__media-column',
    '.product__info-column',
    '.contact-page__content',
    '.page__content',
    '.arc-band',
    '.shop-row__head',
    '.shop-row__brand-head',
    '.site-footer__blocks'
  ].join(',');

  var REVEALED_CLASS = 'is-revealed';
  var root = document.documentElement;
  var observer = null;

  function ready() {
    // Cancel the inline failsafe — this file is alive.
    if (typeof window.__lucMotionReady === 'function') {
      window.__lucMotionReady();
    }
  }

  function reveal(el) {
    el.classList.add(REVEALED_CLASS);
  }

  /** Reveals everything and stops observing. Used as the escape hatch. */
  function revealAll() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    var nodes = document.querySelectorAll(REVEAL_SELECTOR);
    for (var i = 0; i < nodes.length; i++) reveal(nodes[i]);
    root.removeAttribute('data-motion');
  }

  function observe(scope) {
    if (!observer) return;
    var nodes = (scope || document).querySelectorAll(REVEAL_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.classList.contains(REVEALED_CLASS)) continue;
      if (el.dataset.motionObserved === 'true') continue;
      el.dataset.motionObserved = 'true';

      // Anything already on screen at load reveals immediately rather
      // than waiting for a scroll that may never come.
      var box = el.getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) {
        reveal(el);
        continue;
      }
      observer.observe(el);
    }
  }

  function init() {
    // The gate did not open: reduced motion, no IntersectionObserver, or
    // the failsafe already fired. Nothing is hidden, so nothing to do.
    if (root.getAttribute('data-motion') !== 'on') {
      ready();
      return;
    }

    if (!('IntersectionObserver' in window)) {
      revealAll();
      ready();
      return;
    }

    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          reveal(entries[i].target);
          // Once revealed, always revealed — never re-animate.
          observer.unobserve(entries[i].target);
        }
      },
      {
        // Start slightly before the element edge so the arrival feels
        // anticipated rather than triggered.
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.01
      }
    );

    observe(document);
    ready();
  }

  /*
   * Theme Editor: a re-rendered section brings unrevealed markup with
   * it. Observe the new subtree rather than the whole document.
   */
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelectorAll) observe(event.target);
  });

  /*
   * A visitor can turn on "reduce motion" while the page is open. If
   * that happens, stop animating and make sure nothing is left hidden.
   */
  var reduceQuery = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceQuery) {
    var onPreferenceChange = function (event) {
      if (event.matches) revealAll();
    };
    if (reduceQuery.addEventListener) {
      reduceQuery.addEventListener('change', onPreferenceChange);
    } else if (reduceQuery.addListener) {
      // Safari < 14.
      reduceQuery.addListener(onPreferenceChange);
    }
  }

  try {
    init();
  } catch (error) {
    // Never let a motion failure hide the storefront.
    revealAll();
    ready();
  }
})();
